import { DateInput as MantineDateInput } from "@mantine/dates";
import type { DateStringValue } from "@mantine/dates";
import classNames from "classnames";
import { addDays, format } from "date-fns";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/sharp-regular-svg-icons";

import { FormFieldError } from "../internal/form-field-error/form-field-error";
import { FormFieldLabel } from "../internal/form-field-label";
import styles from "./date-input.module.scss";

export type DateInputProps = {
  label?: string;
  placeholder?: string;
  value?: DateStringValue | null;
  onChange?: (value: DateStringValue | null) => void;
  error?: string;
  disabled?: boolean;
  clearable?: boolean;
  valueFormat?: string;
  minDate?: DateStringValue;
  maxDate?: DateStringValue;
  onHelpClicked?: () => void;
};

export const DateInput = ({
  label,
  placeholder,
  value,
  onChange,
  error,
  disabled,
  clearable,
  valueFormat,
  minDate,
  maxDate,
  onHelpClicked,
}: DateInputProps) => {
  const [opened, setOpened] = useState(false);

  const togglePopover = () => {
    if (disabled) return;
    setOpened((prev) => !prev);
  };

  const handlePopoverChange = (nextOpened: boolean) => {
    setOpened(nextOpened);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setOpened(false);
    }
  };

  return (
    <MantineDateInput
      label={label ? <FormFieldLabel label={label} onHelpClicked={onHelpClicked} /> : undefined}
      labelProps={{ labelElement: "div" }}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onKeyDown={handleKeyDown}
      error={
        error ? (
          <span className={styles.errorContent}>
            <FormFieldError error={error} />
          </span>
        ) : undefined
      }
      disabled={disabled}
      clearable={clearable}
      valueFormat={valueFormat}
      minDate={minDate}
      maxDate={maxDate}
      firstDayOfWeek={0}
      weekdayFormat={(date: string) => format(addDays(new Date(date), 1), "EEEEE")}
      rightSectionPointerEvents="all"
      rightSection={
        <button
          type="button"
          className={classNames(styles.calendarButton, { [styles.calendarButtonError]: !!error })}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={togglePopover}
          disabled={disabled}
          aria-label="Open date picker"
          aria-expanded={opened}
          aria-haspopup="dialog"
        >
          <FontAwesomeIcon
            icon={faCalendar}
            className={classNames(styles.calendarIcon, { [styles.calendarIconDisabled]: disabled })}
          />
        </button>
      }
      popoverProps={{
        opened,
        onChange: handlePopoverChange,
        withRoles: true,
        classNames: {
          dropdown: styles.dropdown,
        },
      }}
      classNames={{
        root: styles.root,
        wrapper: styles.wrapper,
        label: styles.label,
        input: classNames(styles.input, {
          [styles.inputError]: !!error,
        }),
        section: styles.section,
        error: styles.error,
        day: styles.day,
      }}
    />
  );
};
